import React from 'react';
import { makeStyles } from '@fluentui/react-components';
import useToolkitTray from '../hooks/useToolkitTray';
import useSavedPrompt from '../hooks/useSavedPrompt';
import usePersona from '../hooks/usePersona';
import KnowledgeSourcesTrayItem from './toolkitTrayItems/knowledgeSources/KnowledgeSourcesTrayItem';
import PersonaTrayItem from './toolkitTrayItems/persona/PersonaTrayItem';
import PromptEnhancerButton from './toolkitTrayItems/promptEnhancer/PromptEnhancerButton';
import SuggestedPromptsTrayItem from './toolkitTrayItems/suggestedPrompts/SuggestedPromptsTrayItem';
import ThinkLongerButton from './toolkitTrayItems/thinkLonger/ThinkLongerButton';

const useStyles = makeStyles({
    tray: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: '8px',
        width: '100%',
        marginBottom: '6px',
        flexWrap: 'wrap',
    },
});

interface ToolkitTrayProps {
    onPromptSelect: (promptText: string) => void;
    canEnhancePrompt: boolean;
    canUndoPromptEnhancement: boolean;
    isPromptEnhancing: boolean;
    onEnhancePrompt: () => void;
    onUndoPromptEnhancement: () => void;
}

const ToolkitTray: React.FC<ToolkitTrayProps> = ({
    onPromptSelect,
    canEnhancePrompt,
    canUndoPromptEnhancement,
    isPromptEnhancing,
    onEnhancePrompt,
    onUndoPromptEnhancement,
}) => {
    const styles = useStyles();
    const vm = useToolkitTray();
    const savedPromptVm = useSavedPrompt();
    const personaVm = usePersona();

    return (
        <div className={styles.tray}>
            <SuggestedPromptsTrayItem
                isOpen={vm.isSuggestedPromptsOpen}
                isDropUp={vm.isDropUp}
                expandCategoriesByDefault={vm.shouldExpandSuggestedPromptCategoriesByDefault}
                userPrompts={savedPromptVm.userPromptOptions}
                suggestedPromptCategories={vm.suggestedPromptCategories}
                savedPromptVm={savedPromptVm}
                onToggleOpen={vm.onToggleSuggestedPromptsOpen}
                onClose={vm.onCloseSuggestedPrompts}
                onPromptSelect={onPromptSelect}
            />
            {vm.canShowKnowledgeSources && (
                <KnowledgeSourcesTrayItem
                    isOpen={vm.isKnowledgeSourcesOpen}
                    isDropUp={vm.isDropUp}
                    agents={vm.knowledgeSourceAgents}
                    selectedAgentIds={vm.selectedKnowledgeSourceIds}
                    areAllSelected={vm.areAllKnowledgeSourcesSelected}
                    onToggleOpen={vm.onToggleKnowledgeSourcesOpen}
                    onClose={vm.onCloseKnowledgeSources}
                    onToggleSelectedAgent={vm.onToggleKnowledgeSource}
                    onToggleAllAgents={vm.onToggleAllKnowledgeSources}
                />
            )}
            {vm.canShowThinkLonger && (
                <ThinkLongerButton isEnabled={vm.isThinkLongerEnabled} onToggle={vm.onToggleThinkLonger} />
            )}
            {vm.canShowPersona && <PersonaTrayItem vm={personaVm} />}
            <PromptEnhancerButton
                canEnhancePrompt={canEnhancePrompt}
                canUndoPromptEnhancement={canUndoPromptEnhancement}
                isPromptEnhancing={isPromptEnhancing}
                onEnhancePrompt={onEnhancePrompt}
                onUndoPromptEnhancement={onUndoPromptEnhancement}
            />
        </div>
    );
};

export default ToolkitTray;
